// Excel-style column filters for list tables: each filterable column has a
// list of its values with tick boxes; ticking several shows rows matching
// ANY of them, and filters on different columns combine (all must match).
// Used with components/ColumnFilter.tsx (the header button + menu and the
// active-filter chips).

import { useMemo, useState, type ReactNode } from 'react';

export const BLANK = '(Blank)';

export interface FilterColumn<T> {
  key:   string;
  label: string;
  /** The value(s) shown for this row in this column. Empty → "(Blank)". */
  get:   (row: T) => string | string[] | null | undefined;
  /** Order of the values in the menu (default: A to Z, numbers by size). */
  sort?: (a: string, b: string) => number;
  /** How a value shows in the menu and chips (e.g. an icon instead of text). */
  render?: (value: string) => ReactNode;
}

export interface FilterOption { value: string; count: number }

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

// Clarity in its natural order (FL best … I3), anything else after, A to Z.
const CLARITY_ORDER = ['FL', 'IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1', 'SI2', 'SI3', 'I1', 'I2', 'I3'];
export function clarityCompare(a: string, b: string) {
  const ia = CLARITY_ORDER.indexOf(a.toUpperCase()), ib = CLARITY_ORDER.indexOf(b.toUpperCase());
  if (ia >= 0 && ib >= 0) return ia - ib;
  if (ia >= 0) return -1;
  if (ib >= 0) return 1;
  return collator.compare(a, b);
}

function valuesOf<T>(col: FilterColumn<T>, row: T): string[] {
  const v = col.get(row);
  const list = Array.isArray(v) ? v : [v];
  const clean = list.map(x => (x == null ? '' : String(x)).trim()).map(x => x || BLANK);
  return clean.length ? clean : [BLANK];
}

export interface ColumnFilters {
  /** Ticked values per column key (absent = no filter on that column). */
  selected: Record<string, string[]>;
  /** The values a column's menu lists, from rows that pass every OTHER column's filter. */
  options:  (key: string) => FilterOption[];
  set:      (key: string, values: string[] | null) => void;
  clear:    () => void;
  active:   { key: string; label: string; values: string[] }[];
  /** Changes whenever the filters change (for paging resets). */
  stamp:    string;
  /** Rows before / after the column filters. */
  total:    number;
  shown:    number;
  /** A value as the column wants it shown (icon or text). */
  show:     (key: string, value: string) => ReactNode;
}

export function useColumnFilters<T>(
  rows: T[], columns: FilterColumn<T>[],
  /** Filters on when the page opens (e.g. Sales starts on Pay Status: Unpaid). */
  initial: Record<string, string[]> = {},
): { rows: T[]; filters: ColumnFilters } {
  const [selected, setSelected] = useState<Record<string, string[]>>(initial);

  // Drop filters for columns that no longer exist (e.g. a column switched off).
  const cols = columns;
  const colByKey = useMemo(() => new Map(cols.map(c => [c.key, c])), [cols]);
  const activeKeys = Object.keys(selected).filter(k => colByKey.has(k));

  const passes = (row: T, skip?: string) => activeKeys.every(k => {
    if (k === skip) return true;
    const want = selected[k];
    return valuesOf(colByKey.get(k)!, row).some(v => want.includes(v));
  });

  const filtered = useMemo(
    () => (activeKeys.length ? rows.filter(r => passes(r)) : rows),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, selected, colByKey],
  );

  const options = (key: string): FilterOption[] => {
    const col = colByKey.get(key);
    if (!col) return [];
    const counts = new Map<string, number>();
    for (const r of rows) {
      if (!passes(r, key)) continue;
      for (const v of new Set(valuesOf(col, r))) counts.set(v, (counts.get(v) ?? 0) + 1);
    }
    // Keep ticked values listed even if nothing has them right now.
    for (const v of selected[key] ?? []) if (!counts.has(v)) counts.set(v, 0);
    const sort = col.sort ?? ((a: string, b: string) => collator.compare(a, b));
    return [...counts.entries()]
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => (a.value === BLANK ? 1 : b.value === BLANK ? -1 : sort(a.value, b.value)));
  };

  const filters: ColumnFilters = {
    selected,
    options,
    set: (key, values) => setSelected(s => {
      const next = { ...s };
      if (values == null) delete next[key]; else next[key] = values;
      return next;
    }),
    clear: () => setSelected({}),
    active: activeKeys.map(k => ({ key: k, label: colByKey.get(k)!.label, values: selected[k] })),
    stamp: JSON.stringify(selected),
    total: rows.length,
    shown: filtered.length,
    show: (key, value) => {
      const r = colByKey.get(key)?.render;
      return r && value !== BLANK ? r(value) : value;
    },
  };
  return { rows: filtered, filters };
}
